import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  DataBridgeVersion,
  ListPageEntry,
  RemoteBoundaryComponent,
  RoutablePageRegistryEntry,
  ModuleRegistryEntry,
  MenuContext,
} from '@dutchiesdk/ecommerce-extensions-sdk';

import type { Dispensaries } from 'src/gql/graphql';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useLazyRemoteModule from 'src/utils/module-federation/use-lazy-remote-module';
import useUI from 'hooks/use-ui';

import { getLivePreviewConfig } from 'src/utils/module-federation/get-live-preview-config';
import {
  CommerceComponents,
  RemoteModuleRegistryWithCustomTagFilters,
  RemoteTheme,
  RouteablePage,
  defaultCommerceComponents,
} from './context';
import { useBridgeDataInterface } from './bridge/bridge-data-interface';

// Becomes the CommerceComponentsContext value for the majority of stores, so it must be
// one shared reference — a fresh spread per call re-renders every consumer on unrelated
// churn. Resolved is true because there is no remote to wait for.
const resolvedDefaultCommerceComponents: CommerceComponents = {
  ...defaultCommerceComponents,
  isRemoteThemeResolved: true,
};

const getMajorVersion = (version: string): number => {
  const majorVersionMatch = /^(\d+)/.exec(version);
  return majorVersionMatch ? parseInt(majorVersionMatch[1], 10) : 0;
};

export const remoteComponentIsValid = (
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Component?: RemoteBoundaryComponent | undefined
): Component is RemoteBoundaryComponent => {
  if (!Component) {
    return false;
  }

  const componentMajorVersion = getMajorVersion(Component.DataBridgeVersion);
  const hostMajorVersion = getMajorVersion(DataBridgeVersion);

  if (componentMajorVersion !== hostMajorVersion) {
    console.warn(
      `Remote component (${Component.name} v${Component.DataBridgeVersion}) is not compatible with Dutchie Commerce Components data bridge host version: ${DataBridgeVersion}`
    );
    return false;
  }
  return true;
};

export function useRemoteCommerceComponents(): {
  getCommerceComponents: (styledTheme?: RemoteTheme) => CommerceComponents;
  remoteThemeModule: any;
  modulePath: string | undefined;
  isRemoteThemeResolved: boolean;
} {
  const [moduleLoadError, setModuleLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const UI = useUI();
  const { isStoreFront, isKiosk } = UI;
  const isStoreFrontOrKiosk = isStoreFront || isKiosk;

  const { dispensary } = useDispensary() as { dispensary?: Dispensaries | undefined };
  const livePreviewConfig = useMemo(() => getLivePreviewConfig(), []);

  // Use window.reactEnv as an early source for the theme config. It's server-injected
  // before React boots, so we know immediately whether a remote theme is configured —
  // without waiting for the dispensary Apollo query to complete.
  const reactEnvThemeConfig = useMemo(
    () => (typeof window !== 'undefined' ? window.reactEnv?.remoteExtensions?.theme : null),
    []
  );
  // Once dispensary has loaded, trust only its data. Before it loads, seed from
  // window.reactEnv so we know a remote is coming before the query round-trip.
  const dispensaryOrEarlyTheme = dispensary ? dispensary.remoteExtensions?.theme : reactEnvThemeConfig;
  const customizationProvider = livePreviewConfig ?? (isStoreFrontOrKiosk ? dispensaryOrEarlyTheme : null);

  // Whether a remote theme is configured for this store. This is knowable during SSR: the
  // dispensary config (including remoteExtensions.theme) is prefetched into the Apollo cache
  // server-side, and window.reactEnv.remoteExtensions.theme is injected before React boots on the
  // client. Because both the server render and the first client render derive the same value, we
  // can seed render state from it without risking a hydration mismatch.
  const isThemeExpected = Boolean(customizationProvider?.modulePath);

  // Holds theme component slots blank until the initial remote fetch settles, preventing the stock
  // Dutchie UI from painting before the customer theme arrives. Module federation is browser-only,
  // so the remote can never resolve during SSR; seeding from isThemeExpected makes the server itself
  // hold the slot (rather than flashing stock UI that the client would immediately replace), and
  // keeps the first client render identical to the server HTML for clean hydration. The client then
  // swaps in the real remote once loadRemote resolves post-mount.
  const [isPendingInitialLoad, setIsPendingInitialLoad] = useState<boolean>(isThemeExpected);

  const isServer = typeof window === 'undefined';
  const skip = isServer || !customizationProvider?.modulePath;

  // If skip transitions to true after mount (e.g. dispensary loaded with no theme config),
  // release the hold so stock/fallback components can render.
  useEffect(() => {
    if (skip) {
      setIsPendingInitialLoad(false);
    }
  }, [skip]);

  const handleLoadFail = useCallback((error: string) => {
    setModuleLoadError(error);
    setIsPendingInitialLoad(false);
  }, []);

  const handleLoading = useCallback((loading: boolean) => {
    if (loading) {
      // Clear any previous error so a successful re-fetch can render the theme.
      setModuleLoadError(null);
    }
    setIsLoading(loading);
    if (!loading) {
      setIsPendingInitialLoad(false);
    }
  }, []);

  const remoteThemeModule = useLazyRemoteModule<{ default?: RemoteModuleRegistryWithCustomTagFilters | undefined }>({
    manifestUrl: customizationProvider?.manifestUrl ?? '',
    modulePath: customizationProvider?.modulePath ?? '',
    scope: customizationProvider?.scope ?? '',
    fallback: { default: undefined },
    skip,
    onLoadFail: handleLoadFail,
    onLoading: handleLoading,
  });

  const wrapComponentFactory = (
    componentEntry?: ModuleRegistryEntry,
    styledTheme: RemoteTheme = null
  ): ((o?: { fallback?: React.FC }) => React.FC) => {
    if (componentEntry && typeof componentEntry !== 'function') {
      let context: MenuContext | null = null;
      if (isKiosk) {
        context = 'kiosk';
      } else if (isStoreFront) {
        context = 'store-front';
      }
      if (!context) {
        componentEntry = undefined;
      } else {
        componentEntry = componentEntry[context];
      }
    }

    const Component = componentEntry;

    const WrappedComponent: React.FC<Record<string, unknown> & { __remoteFallback: React.FC }> = ({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      __remoteFallback: FallbackComponent,
      ...props
    }) => {
      const remoteData = useBridgeDataInterface();

      if (!Component) {
        return <FallbackComponent {...props} />;
      }

      const RemoteComponent = Component as React.FC<any>;
      return (
        <ErrorBoundary
          fallbackRender={({ error }) => {
            console.error(`Remote Component error (${String(Component.name)}):`, error);
            return <FallbackComponent {...props} />;
          }}
        >
          {/* passing data via both __dsdk__dataBridgeData and data props to support legacy usage. __dsdk__dataBridgeData was added in SDK v.0.22.0 */}
          <RemoteComponent theme={styledTheme} __dsdk__dataBridgeData={remoteData} data={remoteData} {...props} />
        </ErrorBoundary>
      );
    };

    return ({ fallback = () => <></> }: { fallback?: React.FC } = {}) => {
      // If there was an error loading the module, return the fallback
      if (moduleLoadError) {
        return fallback;
      }

      // Hold the slot blank while the initial remote fetch (or a reload) is in flight.
      // This prevents the stock Dutchie components from flashing before the theme arrives.
      if (isLoading || isPendingInitialLoad) {
        return () => <></>;
      }

      // If the Component is not defined, return the fallback
      if (!Component) {
        return fallback;
      }

      // If the Component is valid, return it with the fallback bound via props
      if (remoteComponentIsValid(Component)) {
        const BoundComponent: React.FC<any> = (props) => <WrappedComponent __remoteFallback={fallback} {...props} />;
        return BoundComponent;
      }

      // If the component is not valid, return the fallback
      return fallback;
    };
  };

  const wrapIterables = (
    iterables?: ModuleRegistryEntry[],
    styledTheme: RemoteTheme = null
  ): (() => React.ElementType[]) => () =>
    iterables?.map((iterable) => wrapComponentFactory(iterable, styledTheme)()) ?? [];

  const wrapCategoryIterables = (
    iterables?: ListPageEntry[],
    styledTheme: RemoteTheme = null
  ): (() => { category: ListPageEntry['category']; components: React.ElementType[] }[]) => () =>
    iterables?.map((iterable) => ({
      category: iterable.category,
      components: iterable.components.map((component) => wrapComponentFactory(component, styledTheme)()),
    })) ?? [];

  const wrapPages = (
    pages: RoutablePageRegistryEntry[] = [],
    styledTheme: RemoteTheme = null,
    fallback: React.FC = () => <></>
  ): RouteablePage[] =>
    pages.map((page) => ({
      ...page,
      component: ({ fallback: pageFallback = fallback } = {}) =>
        wrapComponentFactory(page.component, styledTheme)({ fallback: pageFallback }),
    }));

  const findPage = (
    pagesContainer,
    path = '',
    fallback: React.FC = () => <></>,
    styledTheme: RemoteTheme = null
  ): React.FC => {
    // If there was an error loading the module, return the fallback
    // return fallback on skip, this implies we don't have a config for this
    if (moduleLoadError || skip) {
      return fallback;
    }

    // Hold pages blank while the initial remote fetch is in flight.
    if (isPendingInitialLoad || !pagesContainer) {
      return () => <></>;
    }

    const page = pagesContainer.find((p) => p.path === path);

    if (!page) {
      return fallback;
    }

    return wrapComponentFactory(page.component, styledTheme)({ fallback });
  };

  const isRemoteThemeResolved = !isPendingInitialLoad && !isLoading;

  const getCommerceComponents = useCallback(
    (styledTheme: RemoteTheme = null): CommerceComponents => {
      if (!customizationProvider) {
        return resolvedDefaultCommerceComponents;
      }

      return {
        components: {
          StoreFrontHeader: wrapComponentFactory(remoteThemeModule.default?.StoreFrontHeader, styledTheme),
          StoreFrontMeta: wrapComponentFactory(remoteThemeModule.default?.StoreFrontMeta, styledTheme),
          StoreFrontNavigation: wrapComponentFactory(remoteThemeModule.default?.StoreFrontNavigation, styledTheme),
          StoreFrontFooter: wrapComponentFactory(remoteThemeModule.default?.StoreFrontFooter, styledTheme),
          GlobalStyles: wrapComponentFactory(remoteThemeModule.default?.GlobalStyles, styledTheme),
          StoreFrontHero: wrapComponentFactory(remoteThemeModule.default?.StoreFrontHero, styledTheme),
          StoreFrontLoyaltyWidget: wrapComponentFactory(
            remoteThemeModule.default?.StoreFrontLoyaltyWidget,
            styledTheme
          ),
          StoreFrontLoginWidget: wrapComponentFactory(remoteThemeModule.default?.StoreFrontLoginWidget, styledTheme),
          StoreFrontCategoryTiles: wrapComponentFactory(
            remoteThemeModule.default?.StoreFrontCategoryTiles,
            styledTheme
          ),
          ListPageHero: wrapComponentFactory(remoteThemeModule.default?.ListPageHero, styledTheme),
          ProductDetailsPrimary: wrapComponentFactory(remoteThemeModule.default?.ProductDetailsPrimary, styledTheme),
          ProductDetailsMeta: wrapComponentFactory(remoteThemeModule.default?.ProductDetailsMeta, styledTheme),
          ProductCard: wrapComponentFactory(remoteThemeModule.default?.ProductCard, styledTheme),
          ProductCardListItem: wrapComponentFactory(remoteThemeModule.default?.ProductCardListItem, styledTheme),
          FilterSidePanel: wrapComponentFactory(remoteThemeModule.default?.FilterSidePanel, styledTheme),
        },
        findPage: ({ path, fallback = () => <></> }) =>
          findPage(remoteThemeModule.default?.RouteablePages, path, fallback, styledTheme),
        iterables: {
          StoreFrontCarouselInterstitials: wrapIterables(
            remoteThemeModule.default?.StoreFrontCarouselInterstitials,
            styledTheme
          ),
          CategoryPageInterstitials: wrapCategoryIterables(
            remoteThemeModule.default?.CategoryPageInterstitials,
            styledTheme
          ),
          CategoryPageSlots: wrapCategoryIterables(remoteThemeModule.default?.CategoryPageSlots, styledTheme),
        },
        pages: ({ fallback = () => <></> } = {}) =>
          wrapPages(remoteThemeModule.default?.RouteablePages, styledTheme, fallback),
        events: remoteThemeModule.default?.events,
        isRemoteThemeResolved,
        themeConfig: {
          customTagFilters: remoteThemeModule.default?.enableCustomTagFilters ?? [],
        },
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [remoteThemeModule, customizationProvider, isRemoteThemeResolved]
  );
  return {
    getCommerceComponents,
    remoteThemeModule,
    modulePath: customizationProvider?.modulePath ?? '',
    isRemoteThemeResolved,
  };
}
